import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';

// Chapel Attendance Selection Component
export function ChapelAttendanceSelection({ user, userType, db, functions, impersonateEmail }) {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [attendance, setAttendance] = useState({});
    const [chapelServices, setChapelServices] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [showLegend, setShowLegend] = useState(false);
    const [showPast, setShowPast] = useState(false);
    const pendingUpdatesRef = useRef(new Set());
    const monthYearFormat = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

    // Check if user is a student (board paying or non-board paying)
    const isStudent = userType === 'board_paying_student' || userType === 'non_board_paying';

    const saveAttendance = useCallback(async () => {
        if (impersonateEmail) {
            alert("Cannot save changes while impersonating another user. Please stop impersonation to save changes.");
            return;
        }

        const attendanceByMonth = {};
        for (const [dateString, dayAttendance] of Object.entries(attendance)) {
            const date = new Date(dateString + 'T00:00:00');
            const monthId = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            if (!attendanceByMonth[monthId]) {
                attendanceByMonth[monthId] = {};
            }
            attendanceByMonth[monthId][dateString] = dayAttendance;
        }

        try {
            const savePromises = Object.entries(attendanceByMonth).map(([monthId, monthAttendance]) => {
                const docRef = doc(db, `users/${user.email}/chapelAttendance/${monthId}`);
                return setDoc(docRef, monthAttendance, { merge: true });
            });

            await Promise.all(savePromises);
            setIsDirty(false);
        } catch (e) {
            console.error('Error saving attendance:', e);
        }
    }, [attendance, user.email, db, impersonateEmail]);

    // Notify global save reminder when isDirty changes
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('saveReminderUpdate', {
            detail: {
                componentId: 'chapel-attendance',
                isDirty,
                saveFunction: isDirty ? (() => saveAttendance()) : null
            }
        }));
    }, [isDirty, saveAttendance]);

    const generateCalendarDays = useCallback(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];

        // Fill in days from previous month if needed
        if (firstDayOfMonth > 0) {
            const prevMonthLastDate = new Date(year, month, 0).getDate();
            const prevMonth = month === 0 ? 11 : month - 1;
            const prevMonthYear = month === 0 ? year - 1 : year;
            for (let i = prevMonthLastDate - firstDayOfMonth + 1; i <= prevMonthLastDate; i++) {
                days.push(new Date(prevMonthYear, prevMonth, i));
            }
        }

        // Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }

        // Fill in days from next month if needed
        const totalCells = Math.ceil((firstDayOfMonth + daysInMonth) / 7) * 7;
        const remainingCells = totalCells - (firstDayOfMonth + daysInMonth);
        const nextMonth = month === 11 ? 0 : month + 1;
        const nextMonthYear = month === 11 ? year + 1 : year;
        for (let i = 1; i <= remainingCells; i++) {
            days.push(new Date(nextMonthYear, nextMonth, i));
        }

        return days;
    }, [currentDate]);

    // Load chapel services configuration for current month
    useEffect(() => {
        const loadServicesForMonth = async (monthId) => {
            try {
                const monthRef = doc(db, 'configuration', 'chapelServices', 'months', monthId);
                const monthSnap = await getDoc(monthRef);

                if (monthSnap.exists()) {
                    const data = monthSnap.data();
                    return data.dailyServices || {};
                } else {
                    return {};
                }
            } catch (error) {
                console.error(`❌ Error loading services for month ${monthId}:`, error);
                return {};
            }
        };

        const loadCurrentMonthServices = async () => {
            const monthId = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
            const monthServices = await loadServicesForMonth(monthId);
            setChapelServices(monthServices);
        };

        loadCurrentMonthServices();
    }, [db, currentDate, generateCalendarDays]);

    // Load attendance data
    useEffect(() => {
        if (!user || !isStudent) return;

        const emailToLoad = impersonateEmail || user.email;
        const calendarDays = generateCalendarDays();
        const monthsToLoad = new Set();

        calendarDays.forEach(date => {
            const monthId = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthsToLoad.add(monthId);
        });

        const attendanceTracker = {};
        const unsubscribes = [];

        const updateAttendance = () => {
            const mergedAttendance = {};
            Object.values(attendanceTracker).forEach(monthData => {
                Object.assign(mergedAttendance, monthData);
            });
            setAttendance(mergedAttendance);
            setIsDirty(false);
        };

        monthsToLoad.forEach(monthId => {
            const docRef = doc(db, `users/${emailToLoad}/chapelAttendance/${monthId}`);
            const unsubscribe = onSnapshot(docRef, (docSnap) => {
                if (docSnap.exists()) {
                    attendanceTracker[monthId] = docSnap.data();
                } else {
                    attendanceTracker[monthId] = {};
                }
                updateAttendance();
            });
            unsubscribes.push(unsubscribe);
        });

        return () => unsubscribes.forEach(unsub => unsub());
    }, [user, currentDate, generateCalendarDays, impersonateEmail, isStudent, db]);

    const handleAttendanceChange = (date, serviceId, status) => {
        if (impersonateEmail) {
            alert("Cannot modify attendance while impersonating another user.");
            return;
        }

        const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const updateKey = `${dateString}-${serviceId}`;

        // Prevent duplicate rapid clicks on the same service date
        if (pendingUpdatesRef.current.has(updateKey)) {
            return;
        }

        // Track this update
        pendingUpdatesRef.current.add(updateKey);

        setIsDirty(true);

        // Use functional update with deep cloning to ensure React recognizes state changes
        setAttendance(currentAttendance => {
            // Deep clone to ensure React recognizes the state change
            const newAttendance = JSON.parse(JSON.stringify(currentAttendance));

            if (!newAttendance[dateString]) {
                newAttendance[dateString] = {};
            }
            newAttendance[dateString][serviceId] = status;

            return newAttendance;
        });

        // Clear the pending update after a brief delay
        setTimeout(() => {
            pendingUpdatesRef.current.delete(updateKey);
        }, 100);
    };

    const calendarDays = generateCalendarDays();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!isStudent) {
        return (
            <div className="max-w-4xl mx-auto bg-white p-6 rounded-2xl shadow-xl border">
                <h2 className="text-2xl font-bold text-[#782953] mb-4">Chapel Attendance</h2>
                <p className="text-gray-600">Chapel attendance tracking is only available for students.</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto bg-white p-2 sm:p-4 rounded-2xl shadow-xl border relative pb-24">
            {showLegend && <ChapelLegendModal onClose={() => setShowLegend(false)} />}

            <div className="flex justify-between items-center mb-4">
                <button
                    onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}
                    className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300"
                >
                    &lt;
                </button>
                <h2 className="text-xl sm:text-3xl font-bold text-[#782953]">
                    Chapel Attendance - {monthYearFormat.format(currentDate)}
                </h2>
                <button
                    onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}
                    className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300"
                >
                    &gt;
                </button>
            </div>

            <div className="grid grid-cols-7 gap-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day =>
                    <div key={day} className="text-center font-bold text-gray-600 mb-1 text-xs sm:text-sm">
                        {day}
                    </div>
                )}

                {calendarDays.map((date, index) => {
                    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    const dayAttendance = attendance[dateString] || {};
                    const dayServices = chapelServices[dateString] || {};
                    // Allow editing for dates up to 30 days in the past
                    const daysAgo = Math.floor((today - date) / (1000 * 60 * 60 * 24));
                    const isEditable = daysAgo <= 30;
                    const isCurrentMonth = date.getMonth() === currentDate.getMonth();

                    if (daysAgo > 30 && !showPast) return <div key={index} className="min-h-[120px]"></div>;

                    return (
                        <div
                            key={dateString}
                            className={`border rounded-md p-1 min-h-[120px] ${
                                !isCurrentMonth ? 'bg-gray-50 opacity-70' : 'bg-white'
                            }`}
                        >
                            <div className="font-bold text-center text-xs mb-1">{date.getDate()}</div>
                            <div className="space-y-1">
                                {Object.entries(dayServices).map(([serviceId, service]) => {
                                    const attendanceStatus = dayAttendance[serviceId] || 'absent';

                                    let statusClass = "bg-red-200 text-red-800"; // Absent
                                    let statusText = "❌";

                                    if (attendanceStatus === 'present') {
                                        statusClass = "bg-green-200 text-green-800";
                                        statusText = "✅";
                                    } else if (attendanceStatus === 'late') {
                                        statusClass = "bg-orange-200 text-orange-800";
                                        statusText = "⏰";
                                    } else if (attendanceStatus === 'excused') {
                                        statusClass = "bg-yellow-200 text-yellow-800";
                                        statusText = "➖";
                                    }

                                    // Shorten service names for display
                                    const shortenServiceName = (name) => {
                                        const shortenings = {
                                            'Liturgy': 'Liturgy',
                                            'Vespers': 'Vespers',
                                            'Matins': 'Matins',
                                            'Akathistos': 'Akathistos',
                                            'Compline': 'Compline',
                                            'Vigil' : 'Vigil',
                                        };

                                        for (const [fullWord, shortWord] of Object.entries(shortenings)) {
                                            if (name.toLowerCase().includes(fullWord.toLowerCase())) {
                                                return shortWord;
                                            }
                                        }
                                        return name; // Return full name if no match
                                    };

                                    const displayName = shortenServiceName(service.name);

                                    // Get status display name for tooltip
                                    const getStatusDisplayName = (status) => {
                                        switch(status) {
                                            case 'present': return 'Present';
                                            case 'late': return 'Late';
                                            case 'absent': return 'Absent';
                                            case 'excused': return 'Excused';
                                            default: return 'Absent';
                                        }
                                    };

                                    return (
                                        <button
                                            key={serviceId}
                                            disabled={!isEditable}
                                            onClick={() => {
                                                const nextStatus = attendanceStatus === 'present' ? 'late' :
                                                                 attendanceStatus === 'late' ? 'absent' :
                                                                 attendanceStatus === 'absent' ? 'excused' : 'present';
                                                handleAttendanceChange(date, serviceId, nextStatus);
                                            }}
                                            className={`w-full h-8 text-xs font-semibold rounded transition-all duration-200 mb-1 ${statusClass} disabled:cursor-not-allowed disabled:opacity-70`}
                                            title={`${service.name} - ${getStatusDisplayName(attendanceStatus)}`}
                                        >
                                            <div className="truncate">
                                                {displayName} - {statusText}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex justify-center items-center gap-4 mt-4">
                <button
                    onClick={() => setShowLegend(true)}
                    className="text-sm text-blue-600 hover:underline"
                >
                    Show Legend
                </button>
                <button
                    onClick={() => setShowPast(!showPast)}
                    className="text-sm text-blue-600 hover:underline"
                >
                    {showPast ? 'Hide' : 'Show'} Past Days
                </button>
            </div>
        </div>
    );
}

// Legend Modal Component
function ChapelLegendModal({ onClose }) {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-30 p-4" onClick={onClose}>
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full" onClick={e => e.stopPropagation()}>
                <h3 className="text-xl font-bold mb-4 text-[#782953]">Chapel Attendance Legend</h3>
                <p className="text-gray-600 mb-4">Click any service button to cycle through attendance options.</p>
                <div className="space-y-3">
                    <div className="flex items-center gap-4">
                        <div className="w-8 h-6 bg-green-200 text-green-800 rounded flex items-center justify-center text-xs font-semibold">✅</div>
                        <span><strong className="font-semibold">Present:</strong> Attended service on time</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-8 h-6 bg-orange-200 text-orange-800 rounded flex items-center justify-center text-xs font-semibold">⏰</div>
                        <span><strong className="font-semibold">Late:</strong> Attended service but arrived late</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-8 h-6 bg-red-200 text-red-800 rounded flex items-center justify-center text-xs font-semibold">❌</div>
                        <span><strong className="font-semibold">Absent:</strong> Not present at service</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-8 h-6 bg-yellow-200 text-yellow-800 rounded flex items-center justify-center text-xs font-semibold">➖</div>
                        <span><strong className="font-semibold">Excused:</strong> Authorized absence</span>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="mt-6 w-full bg-[#782953] text-white font-bold py-2 px-4 rounded-lg hover:bg-opacity-90"
                >
                    Got it!
                </button>
            </div>
        </div>
    );
}
